import { AuthRole } from "@/services/shared/auth/auth.interfaces";
import {
  isAuthenticated,
  logout as apiLogout,
} from "@/services/shared/auth/auth.service";
import { getUserProfile } from "@/services/user/user.service";
import React, { createContext, useContext, useEffect, useReducer } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { LevelTestStates } from "@/services/student/level-test.interfaces";

interface UserState {
  id: string | null;
  email: string | null;
  name: string | null;
  role: AuthRole | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  levelTestState?: LevelTestStates;
}

interface UserAction {
  type: string;
  payload?: unknown;
}

interface PrivateContextType {
  state: UserState;
  dispatch: React.Dispatch<UserAction>;
}

const PrivateContext = createContext<PrivateContextType | null>(null);

const initialState: UserState = {
  id: null,
  email: null,
  name: null,
  role: AuthRole.None,
  isLoggedIn: false,
  isLoading: false,
  isLoaded: false,
  levelTestState: LevelTestStates.Inactive,
};

const reducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case "SET_LOADING":
      return {
        ...state,
        isLoading: true,
      };

    case "CLEAR_LOADING":
      return {
        ...state,
        isLoading: false,
      };

    case "SET_LOADED":
      return {
        ...state,
        isLoaded: true,
      };

    case "CLEAR_LOADED":
      return {
        ...state,
        isLoaded: false,
      };

    case "SET_USER":
      return {
        ...state,
        ...(action.payload as Partial<UserState>),
        isLoggedIn: true,
      };
    case "CLEAR_USER":
      return {
        ...initialState,
      };
    case "UPDATE_USER":
      return {
        ...state,
        ...(action.payload as Partial<UserState>),
      };
    default:
      return state;
  }
};

export const PrivateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    _refreshData(state, dispatch);
  }, []);

  return (
    <PrivateContext.Provider value={{ state, dispatch }}>
      {state.isLoading && <h1>Loading...</h1>}
      {/* {!state.isLoading && children} */}
      {children}
    </PrivateContext.Provider>
  );
};

export const PrivateRouteChecker = (props: { userRole?: AuthRole }) => {
  const { userRole } = props;
  const { isLoaded, isLoggedIn, role } = usePrivate();
  const location = useLocation();
  const ALLOWED_LOCATIONS = ["/home", "/login", "/signup", "/logout"];

  if (isLoaded) {
    if (!isLoggedIn) {
      if (!ALLOWED_LOCATIONS.includes(location.pathname)) {
        return <Navigate to="/login" />;
      }
    }

    if (role !== null && role !== userRole) {
      return <Navigate to="/login" />;
    }
  }

  return null;
};

export const LevelTestChecker = () => {
  const { isLoaded, isLoggedIn, role, levelTestState } = usePrivate();
  const location = useLocation();

  if (!isLoaded || !isLoggedIn || role !== AuthRole.Student) return null;

  // Permite todas as rotas sob /student/level-test/* (inclui welcome, test, result, etc.)
  const isOnAllowedLevelTestPage = location.pathname.startsWith("/student/level-test/");

  // Rotas que devem ser permitidas mesmo com sessão ativa de teste de nivelamento
  // Essas rotas são essenciais para o fluxo de compra e configuração do usuário
  const allowedRoutesWithActiveTest = [
    "/student/checkout",           // Página de checkout (inclui /checkout/:packageId e /checkout/success)
    "/student/cart",                // Carrinho de compras
    "/student/packages",            // Lista de pacotes (inclui /packages/:packageId)
    "/student/voucher",             // Página de voucher (pode ser usada para desbloquear pacotes)
    "/student/settings",             // Página de configurações do usuário
    "/student/profile",             // Página de perfil do usuário
  ];

  // Verifica se está em uma rota permitida mesmo com teste ativo
  const isOnAllowedRoute = allowedRoutesWithActiveTest.some(route => 
    location.pathname.startsWith(route)
  );

  // Se há uma sessão ativa (Active ou InProgress) e NÃO está em uma das páginas permitidas
  const hasActiveSession = 
    levelTestState === LevelTestStates.Active || 
    levelTestState === LevelTestStates.InProgress;

  if (hasActiveSession && !isOnAllowedLevelTestPage && !isOnAllowedRoute) {
    // Se já tem respostas (InProgress), vai direto para o teste
    // Se não tem respostas (Active), vai para a página de boas-vindas
    const targetPath = levelTestState === LevelTestStates.InProgress 
      ? "/student/level-test/test"
      : "/student/level-test/welcome";
    
    return <Navigate to={targetPath} replace />;
  }

  return null;
};

const _refreshData = async (
  state: UserState,
  dispatch: React.Dispatch<UserAction>
) => {
  const isLoggedIn = isAuthenticated();

  if (isLoggedIn) {
    dispatch({ type: "CLEAR_LOADED" });
    dispatch({ type: "SET_LOADING" });
    console.log("Calling getUserProfile...");
    getUserProfile()
      .then((response) => {
        const [success, data] = response;

        const userData: Partial<UserState> = {
          id: data?.id,
          email: data?.email,
          name: data?.name,
          role: data?.role as AuthRole,
          levelTestState: data?.levelTestState as LevelTestStates,
        };

        if (userData.id) {
          dispatch({ type: "SET_USER", payload: userData });
        } else {
          dispatch({ type: "CLEAR_USER" });
        }

        console.log("getUserProfile", { success, data });
      })
      .catch((error) => {
        console.error("Error calling getUserProfile:", error);
        dispatch({ type: "CLEAR_USER" });
      })
      .finally(() => {
        dispatch({ type: "CLEAR_LOADING" });
        dispatch({ type: "SET_LOADED" });
      });
  } else {
    // If not logged in, mark as loaded immediately so authentication check can proceed
    dispatch({ type: "CLEAR_USER" });
    dispatch({ type: "SET_LOADED" });
  }
};

export const usePrivate = () => {
  const context = useContext(PrivateContext);
  if (!context) {
    throw new Error("usePrivate must be used within a PrivateProvider");
  }
  const { state, dispatch } = context;

  const setUser = (userData: Partial<UserState>) => {
    dispatch({ type: "SET_USER", payload: userData });
  };

  const clearUser = () => {
    dispatch({ type: "CLEAR_USER" });
  };

  const updateUser = (userData: Partial<UserState>) => {
    dispatch({ type: "UPDATE_USER", payload: userData });
  };

  const refreshData = () => {
    _refreshData(state, dispatch);
  };

  const logout = () => {
    apiLogout();
    clearUser();
  };

  return {
    ...state,
    setUser,
    clearUser,
    updateUser,
    logout,
    refreshData,
  };
};

export const SessionRefresh = () => {
  const { refreshData } = usePrivate();

  useEffect(() => {
    refreshData();
  }, []);

  return null;
};

export const RedirectToHomeIfLoggedIn = () => {
  const { isLoggedIn } = usePrivate();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/home");
    }
  }, [isLoggedIn]);

  return null;
};
